import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { ModalSheet } from '@/shared/ui/ModalSheet'
import { ModalSheetContext } from '../lib/ModalSheetContext'
import { useBool } from '@/shared/lib/useBool'

export const ModalSheetProvider = ({ children }) => {
  const { value: isOpen, onFalse: onClose, onTrue: onOpen } = useBool(false)
  const [props, setProps] = useState({})
  const [content, setContent] = useState(null)
  const [onCloseExternal, setOnCloseExternal] = useState(null)
  const [nextData, setNextData] = useState(null)
  const [id, setId] = useState(null)

  const openModal = useCallback(
    ({ content, props, onClose, id }) => {
      setContent(content)
      setProps(props)
      setOnCloseExternal(() => onClose)
      setId(id)
      onOpen()
    },
    [onOpen],
  )

  const updateModal = useCallback(({ content, props, onClose, id }) => {
    setContent(content)
    setProps(props)
    setOnCloseExternal(() => onClose)
    setId(id)
  }, [])

  const closeModal = useCallback(() => {
    onClose()
    onCloseExternal?.()
    setContent(null)
    setProps({})
    setOnCloseExternal(null)
    setId(null)
  }, [onClose, onCloseExternal])

  const toggleModal = useCallback(
    ({ content: externalContent, props, onClose, id }) => {
      if (!isOpen) {
        openModal({ content: externalContent, props, onClose, id })
      } else {
        const isSameContent =
          content?.type === externalContent?.type && content?.key === externalContent?.key
        if (!isSameContent) setNextData({ content: externalContent, props, onClose, id })
        closeModal()
      }
    },
    [isOpen, content, openModal, closeModal],
  )

  useEffect(() => {
    if (nextData && !isOpen) {
      openModal(nextData)
      setNextData(null)
    }
  }, [nextData, openModal, isOpen])

  const isFullScreenOpen = useMemo(() => {
    return isOpen && props?.isFullScreen
  }, [isOpen, props?.isFullScreen])

  return (
    <ModalSheetContext.Provider
      value={{ openModal, updateModal, closeModal, toggleModal, isFullScreenOpen, id }}
    >
      {children}
      <ModalSheet isOpen={isOpen} onClose={closeModal} instantOpen {...props}>
        {content}
      </ModalSheet>
    </ModalSheetContext.Provider>
  )
}
